<template>
    <BasePage v-if="model.invalidOrder" :model="contentPageModel" box-shadow>
        <span>{{ model.translations['orderConfirmation.InvalidOrder'] }}</span>
    </BasePage>
    <BasePage v-if="model.isDonation && !model.invalidOrder" :model="contentPageModel" box-shadow>
        <div class="order-confirmation">
            <h3>{{ model.translations['orderConfirmation.Order.SectionTitle'] }}{{ model.orderNumber }}</h3>
            <div class="grid-view">
                <div>{{ model.translations['orderConfirmation.Order.Description'] }}</div>
                <Input type="text" readonly :model-value="model.orderNumber" />
                <template v-if="model.transactionId">
                    <div>{{ model.translations['donation.Confirmation.TransactionId'] }}</div>
                    <div>
                        {{ model.transactionId }}
                    </div>
                </template>
                <div>{{ model.translations['donation.Confirmation.OrderSummary'] }}</div>
                <div>
                    {{ model.amountSummary }}
                </div>
                <template v-if="model.giftAidSummary">
                    <div>{{ model.translations['donation.Confirmation.GiftAidSummary'] }}</div>
                    <div>
                        {{ model.giftAidSummary }}
                    </div>
                </template>
            </div>
            <h3>{{ model.personalDetails.translations['orderConfirmation.PersonalDetails.SectionTitle'] }}</h3>
            <div class="flex-view">
                {{ model.personalDetails.selectedTitle }}
                {{ model.personalDetails.firstName }}
                {{ model.personalDetails.lastName }}
            </div>
            <h3>{{ model.contactInformation.translations['orderConfirmation.Contact.SectionTitle'] }}</h3>
            <div class="grid-view">
                <div>Email</div>
                <div>{{ model.contactInformation.email }}</div>
                <div>Phone</div>
                <div>{{ model.contactInformation.phone }}</div>
            </div>
            <h3>{{ model.addressInformation.translations['orderConfirmation.AddressInformation.SectionTitle'] }}</h3>
            <div class="flex-view">
                <div class="grid-view">
                    <div>Address</div>
                    <div>{{ model.addressInformation.addressLine1 }} {{ model.addressInformation.addressLine2 }} {{
                        model.addressInformation.addressLine3 }}</div>
                    <div>City</div>
                    <div>
                        {{ model.addressInformation.province }}
                        {{ model.addressInformation.city }}
                        {{ model.addressInformation.postcode }}
                    </div>
                </div>
            </div>
            <template v-if="model.additionalInformation.otherDetails">
                <h3>{{ model.additionalInformation.translations['orderConfirmation.AdditionalInformation.SectionTitle']
                }}</h3>
                <p>{{ model.additionalInformation.otherDetails }}</p>
            </template>
        </div>
    </BasePage>
    <BasePage v-if="!model.isDonation && !model.invalidOrder" :model="contentPageModel" box-shadow>
        <div class="order-confirmation">
            <h2>{{ model.translations['orderConfirmation.SectionTitle'] }}</h2>
            <p>{{ model.translations['orderConfirmation.SectionDescription'] }}</p>
            <div class="grid-view">
                <div>{{ model.translations['orderConfirmation.Order.Description'] }}</div>
                <Input type="text" readonly :model-value="model.orderNumber" />
                <template v-if="model.amountSummary">
                    <div>{{ model.translations['orderConfirmation.Email.DonationLabel'] }}</div>
                    <div>
                        {{ model.amountSummary }}
                    </div>
                </template>
            </div>
            <h3>{{ model.personalDetails.translations['orderConfirmation.PersonalDetails.SectionTitle'] }}</h3>
            <div class="flex-view">
                {{ model.personalDetails.selectedTitle }}
                {{ model.personalDetails.firstName }}
                {{ model.personalDetails.lastName }}
            </div>
            <h3>{{ model.contactInformation.translations['orderConfirmation.Contact.SectionTitle'] }}</h3>
            <div class="flex-view">{{ model.contactInformation.email }} {{ model.contactInformation.phone }}</div>
            <h3>{{ model.addressInformation.translations['orderConfirmation.AddressInformation.SectionTitle'] }}</h3>
            <div class="flex-view">
                <div class="flex-view">
                    {{ model.addressInformation.addressLine1 }}
                    {{ model.addressInformation.addressLine2 }}
                    {{ model.addressInformation.addressLine3 }}
                </div>
                <div class="flex-view">
                    {{ model.addressInformation.city }}
                    {{ model.addressInformation.province }},
                    {{ model.addressInformation.postcode }}
                </div>
            </div>
            <h3>{{ model.collectedItems.translations['orderConfirmation.CollectedItems.SectionTitle'] }}</h3>
            <div class="flex-view-col">

                <div> {{ model.collectedItems.translations['orderConfirmation.Email.YourItems'] }} {{ summary }}
                    <template v-if="summary > 1">
                        {{ model.collectedItems.translations['generic.ChristmasTreePlural'] }}
                    </template>
                    <template v-else>
                        {{ model.collectedItems.translations['generic.ChristmasTreeSingular'] }}
                    </template>
                </div>

                <span>{{ descriptionPostfix }}</span>
            </div>
            <template v-if="model.additionalInformation.otherDetails">
                <h3>
                    {{
                        model.additionalInformation.translations['orderConfirmation.AdditionalInformation.SectionTitle'] }}
                </h3>
                <p>{{ model.additionalInformation.otherDetails }}</p>
            </template>
        </div>
    </BasePage>
</template>
<script lang="ts" setup>
import BasePage from '@/components/Common/BasePage/BasePage.vue';
import { IOrderConfirmationProps } from './OrderConfirmation.model';
import { computed } from 'vue';
import { IHeroSection } from '@/models/model';
import { Input } from '@/components/ui/input';

const { model } = defineProps<IOrderConfirmationProps>()



const contentPageModel = computed(() => {
    const pageModel: IHeroSection = {
        title: model.title,
    }

    if (model.isDonation) {
        pageModel.description =
            model.translations["orderConfirmation.SectionDonationDescription"]
    }

    if (model.hospiceInfo) {
        pageModel.image = { url: model.hospiceInfo.logoUrl, text: "" }
        pageModel.links = [
            {
                url: model.hospiceInfo.url,
                target: "_blank",
                text: model.translations["hospiceList.ViewHospiceDetailPageButtonText"],
            },
        ]
    }

    return pageModel
})

const summary = computed(() => {
    return model.collectedItems.collectedItems
        .map((x) => x.count)
        .reduce((prev, curr) => Number(prev) + Number(curr), 0)
})

const descriptionPostfix = computed(() => {
    const bigTree = model.collectedItems.collectedItems.find((x) => x.id === model.collectedItems.bigTreeSku)
    const smallTree = model.collectedItems.collectedItems.find((x) => x.id !== model.collectedItems.bigTreeSku)

    if (!bigTree || Number(bigTree.count) <= 0) return ""
    if (!smallTree || Number(smallTree.count) === 0) {
        return model.collectedItems.translations["orderConfirmation.Email.OnlyOneTreeWhichIsLarge"]
    }
    return model.collectedItems.translations["orderConfirmation.Email.ManyTreesIncludingLarge"]
})

</script>
<style lang="scss" scoped>
.order-confirmation {
    display: flex;
    flex-direction: column;
    font-size: 1.15rem;
    padding: 24px 30px;
    border-radius: 0 0 24px 24px;
    border: 1px solid lightgray;
    border-top: 0;
    gap: 15px;
}

.grid-view {
    display: grid;
    grid-template-columns: 1fr;
    gap: 10px;

    div:nth-of-type(even) {
        font-weight: 600;
    }
}

h3 {
    font-weight: 700;
    font-size: 1.75rem;
}

.flex-view-col {
    display: flex;
    flex-direction: column;
}

.flex-view {
    display: flex;
}

@media (min-width: 768px) {
    .grid-view {
        grid-template-columns: max-content 1fr;
    }
}

input {
    &:focus-visible {
        box-shadow: unset;
    }
}
</style>